import type { FormComponent, FormFlowStep } from './types/formTypes';

export interface PersistedFormField {
  id: string;
  component_id?: string | null;
  type: string;
  label?: string;
  required?: boolean;
  properties?: Record<string, unknown>;
}

export interface PersistedFormSchema {
  [key: string]: unknown;
  fields: PersistedFormField[];
}

/** Runtime behavior comes from field_type/type, never from the tenant-owned slug. */
export function serializeFormSteps(steps: FormFlowStep[]): PersistedFormSchema {
  return {
    fields: steps.map((step) => {
      const componentId = step.component.persistedComponentId
        ?? (step.component.id.startsWith('legacy-component-') ? undefined : step.component.id);
      return {
        id: step.id,
        ...(componentId ? { component_id: componentId } : {}),
        type: step.component.fieldType ?? 'text_field',
        label: String(step.properties.label ?? ''),
        required: Boolean(step.properties.required),
        properties: { ...step.properties },
      };
    }),
  };
}

export function restoreFormSteps(
  schema: unknown,
  displayType: (fieldType: string) => string,
  displayField?: (field: PersistedFormField, index: number) => FormFlowStep['display'],
): FormFlowStep[] {
  const fields = schema && typeof schema === 'object' && Array.isArray((schema as PersistedFormSchema).fields)
    ? (schema as PersistedFormSchema).fields
    : [];
  return fields.map((field, index) => {
    const properties = {
      label: field.label ?? '',
      required: field.required ?? false,
      colSpan: 12,
      ...(field.properties ?? {}),
    };
    const component: FormComponent = {
      id: field.component_id || `legacy-component-${index}`,
      ...(field.component_id ? { persistedComponentId: field.component_id } : {}),
      name: field.component_id || field.type,
      fieldType: field.type,
      displayName: displayType(field.type),
      description: '',
      icon: field.type === 'file_attachment'
        ? 'FileUpload'
        : field.type === 'organization_location'
          ? 'LocationOnIcon'
          : 'TextFields',
      properties,
    };
    return {
      id: field.id || `step-restored-${index}`,
      type: 'form' as const,
      component,
      position: { x: 0, y: index * 80 },
      properties,
      display: displayField?.(field, index),
      connections: [],
    };
  });
}

function canonicalType(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const code = value.trim().toLowerCase().replace(/[- ]/g, '_');
  if (code === 'file_attachment') return code;
  return null;
}

/** Mirrors Backend validate_no_task_attachment_configuration(). */
export function hasTaskAttachmentConfiguration(flow: unknown): boolean {
  const walk = (value: unknown, taskForm = false): boolean => {
    if (Array.isArray(value)) return value.some((child) => walk(child, taskForm));
    if (!value || typeof value !== 'object') return false;
    const record = value as Record<string, unknown>;
    const fields = record.fields;
    if (Array.isArray(fields)) {
      for (const rawField of fields) {
        if (!rawField || typeof rawField !== 'object') continue;
        const field = rawField as Record<string, unknown>;
        const properties = field.properties && typeof field.properties === 'object'
          ? field.properties as Record<string, unknown>
          : {};
        const scope = String(field.scope ?? properties.scope ?? '').toLowerCase();
        if ((taskForm || scope === 'task' || scope === 'completion') && canonicalType(field.type) === 'file_attachment') {
          return true;
        }
      }
    }
    return Object.entries(record).some(([key, child]) => walk(
      child,
      taskForm || ['task_form_schema', 'completion_form_schema', 'taskFormSchema'].includes(key),
    ));
  };
  return walk(flow);
}
